import { drawExpiryWeekday, expiryWeekday } from './expiryWeekday.js';
// Rendering RASTER dell'etichetta (Clabel CT221B via TSPL `BITMAP`).
//
// Perché raster: la testina, col comando `TEXT`, ha solo font bitmap ROM a
// celle fisse (min 8×12 dot, niente scala, accenti strippati). Disegnando
// invece l'intera etichetta su canvas alla risoluzione della stampante e inviandola come immagine
// 1-bit col comando `BITMAP`, abbiamo font liberi e di qualsiasi dimensione,
// accenti, word-wrap reale e QR esatto. Il fix nativo del plugin Bluetooth
// (UTF-8→ISO-8859-1, patches/) rende il trasferimento binario affidabile.
//
// La STESSA funzione `renderLabelCanvas` alimenta sia la stampa sia
// l'anteprima ⇒ WYSIWYG reale, identico al pixel.

import QRCode from 'qrcode';
import { toUserMessage, UserFacingError } from '../../errors.js';
import { rectangularQr } from './rectangularQr.js';
import type { LabelTemplate, LabelElement } from '@trackeat/shared';
import { resolveBinding } from './renderer.js';

const DPI = 203;

export interface RasterOptions {
  dpi?: number;
  bitmapMode?: 0 | 1;
  legacyMargins?: boolean;
  /** Layout warnings are informational, including during printing. */
  onIssue?: (message: string) => void;
}
const MM_PER_INCH = 25.4;

export const mmToDots = (mm: number, dpi = DPI): number => Math.round((mm / MM_PER_INCH) * dpi);

const FONT_STACK = '"Helvetica Neue","Helvetica","Arial","Liberation Sans",sans-serif';

// Margine di sicurezza testina Clabel CT221B: il contenuto è SPOSTATO (non
// scalato) di 1 mm dal lato lungo (X) e 1 mm dal lato corto (Y), così il
// bordo rientra nell'area stampabile MANTENENDO i caratteri 1:1 (pt → mm
// reali). Regolabile qui se serve aggiustare di un filo.
export const OFFSET_X_MM = 1;
export const OFFSET_Y_MM = 1;
// Restringimento (non sposta nulla: ancorato all'offset, tira dentro solo il
// bordo destro/basso di questi mm) così anche dx/giù rientrano nell'area
// stampabile senza toccare il posizionamento. Regolabile.
export const SHRINK_X_MM = 3; // lato lungo 50 mm
export const SHRINK_Y_MM = 2; // lato corto 30 mm

function resolveText(el: LabelElement, data: Record<string, unknown>): string {
  if (el.binding) return resolveBinding(el.binding, data);
  if (el.static_value) return el.static_value;
  return '';
}

// Word-wrap con misura reale del font (CanvasRenderingContext2D.measureText).
// Spezza anche le parole singole più larghe del box (es. codici lunghi).
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const rawLine of text.split('\n')) {
    const words = rawLine.split(/\s+/).filter(Boolean);
    let cur = '';
    const pushHardBreak = (word: string): void => {
      let chunk = '';
      for (const ch of word) {
        if (ctx.measureText(chunk + ch).width > maxW && chunk) {
          out.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      }
      cur = chunk;
    };
    for (const w of words) {
      const trial = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(trial).width <= maxW) {
        cur = trial;
      } else if (!cur && ctx.measureText(w).width > maxW) {
        pushHardBreak(w);
      } else {
        if (cur) out.push(cur);
        if (ctx.measureText(w).width > maxW) {
          pushHardBreak(w);
        } else {
          cur = w;
        }
      }
    }
    out.push(cur);
  }
  return out;
}

interface QrSym { modules: { size: number; data: Uint8Array } }
const qrCreate = (d: string): QrSym =>
  (QRCode as unknown as { create: (s: string, o: { errorCorrectionLevel: string }) => QrSym })
    .create(d, { errorCorrectionLevel: 'L' });

function qrMinimumCell(dpi: number): number {
  return Math.ceil(3 * dpi / 203);
}

/** Advisory width including four-module quiet zones; never a print gate. */
export function recommendedStandardQrWidthMm(value: string, options: RasterOptions = {}): number {
  const dpi = options.dpi === 300 ? 300 : 203;
  return (qrCreate(value).modules.size + 8) * qrMinimumCell(dpi) * MM_PER_INCH / dpi;
}

// Ornamento personalizzato: ancora stilizzata + tre onde, 1-bit nero pieno.
// Usato da elementi `text` con `static_value === '@anchor_waves'`. L'area
// (x,y,w,h) viene divisa così:
//   - 65% in alto per l'ancora
//   - 35% in basso per le onde
// Linee sufficientemente spesse (≥2 dot) da uscire nitide su 203 DPI.
function drawAnchorAndWaves(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: { waves?: boolean },
): void {
  const withWaves = opts?.waves !== false;
  const stroke = Math.max(2, Math.round(Math.min(w, h) / 28));
  // Centro orizzontale
  const cx = x + w / 2;

  // ── ANCORA ─────────────────────────────────────────────────────────────
  // Senza onde l'ancora occupa l'intera area (con piccolo padding); con
  // onde lascia 38% in basso per le onde.
  const aH = withWaves ? Math.round(h * 0.62) : Math.round(h * 0.96);
  const aY = y;
  // Anello in alto
  const ringR = Math.max(2, Math.round(aH * 0.10));
  const ringCY = aY + ringR + 1;
  ctx.lineWidth = stroke;
  ctx.strokeStyle = '#000';
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.arc(cx, ringCY, ringR, 0, Math.PI * 2);
  ctx.stroke();
  // Stelo verticale
  const stemTop = ringCY + ringR + 1;
  const stemBottom = aY + aH - Math.max(1, Math.round(aH * 0.08));
  ctx.beginPath();
  ctx.moveTo(cx, stemTop);
  ctx.lineTo(cx, stemBottom);
  ctx.stroke();
  // Crossbar
  const crossY = stemTop + Math.max(2, Math.round(aH * 0.12));
  const crossHalf = Math.round(aH * 0.22);
  ctx.beginPath();
  ctx.moveTo(cx - crossHalf, crossY);
  ctx.lineTo(cx + crossHalf, crossY);
  ctx.stroke();
  // Curve laterali a U (le marre) — un solo arco a forma di U che si apre.
  const armR = Math.round(aH * 0.30);
  const armCY = stemBottom - armR + Math.max(1, Math.round(armR * 0.1));
  ctx.beginPath();
  ctx.arc(cx, armCY, armR, 0, Math.PI, false);
  ctx.stroke();
  // Punte laterali (piccoli "uncini")
  const tipLen = Math.max(2, Math.round(aH * 0.10));
  const tipY = armCY + 1;
  ctx.beginPath();
  ctx.moveTo(cx - armR, tipY);
  ctx.lineTo(cx - armR - tipLen, tipY - tipLen);
  ctx.moveTo(cx + armR, tipY);
  ctx.lineTo(cx + armR + tipLen, tipY - tipLen);
  ctx.stroke();

  if (!withWaves) return;
  // ── ONDE (tre sinusoidi sovrapposte) ───────────────────────────────────
  const wY = aY + aH + Math.max(2, Math.round(h * 0.04));
  const wH = h - (wY - y);
  const waves = 3;
  const padX = Math.round(w * 0.06);
  const lineSpacing = Math.max(stroke + 1, Math.floor(wH / (waves + 0.5)));
  const amp = Math.max(2, Math.floor(lineSpacing * 0.55));
  for (let i = 0; i < waves; i++) {
    const baseY = wY + amp + i * lineSpacing;
    if (baseY + amp > y + h) break;
    ctx.beginPath();
    const steps = 32;
    const left = x + padX;
    const right = x + w - padX;
    const span = right - left;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const px = left + span * t;
      const py = baseY + Math.sin(t * Math.PI * 2) * amp;
      if (s === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
}

/**
 * Renderizza l'etichetta su un canvas alla risoluzione della stampante (1 dot = 1 px). Stesso
 * output usato per la stampa (1-bit) e per l'anteprima fedele.
 */
export function renderLabelCanvas(
  template: LabelTemplate,
  data: Record<string, unknown>,
  options: RasterOptions = {},
): HTMLCanvasElement {
  const dpi = options.dpi === 300 ? 300 : 203;
  const mmToDots = (mm: number): number => Math.round((mm / MM_PER_INCH) * dpi);
  const ptToPx = (pt: number): number => Math.max(6, Math.round((pt * dpi) / 72));
  const issue = (message: string): void => { options.onIssue?.(message); };
  const W = mmToDots(template.width_mm);
  const H = mmToDots(template.height_mm);
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d');
  if (!ctx) return cv;

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#000';
  ctx.textBaseline = 'top';

  // Spostamento di 1 mm dal lato lungo/corto (margine alto-sinistra).
  if (options.legacyMargins !== false) {
    ctx.translate(mmToDots(OFFSET_X_MM), mmToDots(OFFSET_Y_MM));
    // Preserve the existing Clabel geometry; Y428BT uses physical millimetres.
    const sx = (W - mmToDots(SHRINK_X_MM)) / W;
    const sy = (H - mmToDots(SHRINK_Y_MM)) / H;
    ctx.scale(sx, sy);
  }

  for (const el of template.elements) {
    const x = mmToDots(el.x_mm);
    const y = mmToDots(el.y_mm);

    if (el.type === 'text') {
      const txt = resolveText(el, data).trim();
      if (!txt) continue;

      // Marker speciale: "@anchor_waves" disegna ancora + onde,
      // "@anchor" solo ancora a tutta altezza. Area = x/y/w/h.
      if ((txt === '@anchor_waves' || txt === '@anchor') && el.width_mm && el.height_mm) {
        drawAnchorAndWaves(
          ctx,
          x,
          y,
          mmToDots(el.width_mm),
          mmToDots(el.height_mm),
          { waves: txt === '@anchor_waves' },
        );
        continue;
      }
      const adaptive = el.text_fit === 'shrink' || (el.text_fit == null && /{{\s*product(?:\s|[}|])/.test(el.binding ?? ''));
      let px = ptToPx(el.font_size_pt ?? 8);
      const weight = el.font_weight === 'bold' ? '700' : '400';
      ctx.font = `${weight} ${px}px ${FONT_STACK}`;
      let lineH = Math.round(px * 1.2);
      if (adaptive || (el.width_mm && el.width_mm > 0)) {
        let maxW = mmToDots(Math.min(el.width_mm ?? template.width_mm - el.x_mm, template.width_mm - el.x_mm));
        // The legacy text transform shrinks coordinates, while QR sizes stay
        // physical. Reserve the actual quiet zone before wrapping ordinary text.
        const transform = ctx.getTransform();
        const textTop = transform.d * y + transform.f;
        const textHeight = Math.min(mmToDots(el.height_mm ?? template.height_mm - el.y_mm),
          wrapText(ctx, txt, Math.max(1, maxW)).length * lineH);
        const textBottom = textTop + transform.d * textHeight;
        const textLeft = transform.a * x + transform.e;
        for (const qr of template.elements.filter(element => element.type === 'qr')) {
          if (!resolveText(qr, data)) continue;
          const qrLeft = transform.a * mmToDots(qr.x_mm) + transform.e;
          const qrTop = transform.d * mmToDots(qr.y_mm) + transform.f;
          const qrBottom = qrTop + mmToDots(qr.qr_format === 'rmqr' ? qr.height_mm ?? 9 : qr.width_mm ?? 20);
          if (!adaptive && textTop < qrBottom && textBottom > qrTop && textLeft < qrLeft) {
            maxW = Math.min(maxW, Math.floor((qrLeft - textLeft - 1) / transform.a));
          }
        }
        // Without an explicit height, use free space down to the next field.
        const nextY = template.elements.filter(other => other.id !== el.id && other.y_mm > el.y_mm
          && other.x_mm < el.x_mm + maxW * MM_PER_INCH / dpi
          && other.x_mm + (other.width_mm ?? template.width_mm - other.x_mm) > el.x_mm)
          .reduce((bottom, other) => Math.min(bottom, other.y_mm), template.height_mm);
        const maxH = adaptive ? mmToDots(Math.min(el.height_mm ?? nextY - el.y_mm, template.height_mm - el.y_mm))
          : el.height_mm ? mmToDots(el.height_mm) : Number.POSITIVE_INFINITY;
        let lines = wrapText(ctx, txt, Math.max(1, maxW));
        if (adaptive) {
          const minPx = Math.min(px, ptToPx(4));
          while (px > minPx && (lines.length * lineH > maxH || lines.some(line => ctx.measureText(line).width > maxW))) {
            px -= 1;
            ctx.font = `${weight} ${px}px ${FONT_STACK}`;
            lineH = Math.round(px * 1.2);
            lines = wrapText(ctx, txt, Math.max(1, maxW));
          }
          if (lines.length * lineH > maxH || lines.some(line => ctx.measureText(line).width > maxW)) {
            issue('Nome/testo incompleto: aumenta larghezza o altezza della casella, oppure libera spazio nel layout.');
          }
          if (el.y_mm + lines.length * lineH * MM_PER_INCH / dpi > nextY + 0.2) {
            issue('Il testo adattivo copre un campo sottostante: sposta quel campo o riduci la casella.');
          }
        }
        const maxLines = Math.max(1, Math.floor(maxH / lineH));
        if (lines.length * lineH > maxH && /{{\s*allergens(?:\s|[}|])/.test(el.binding ?? '')) {
          issue('Allergeni incompleti: aumenta altezza o larghezza della casella, oppure libera spazio nel layout.');
        }
        if (lines.length > maxLines) {
          lines = lines.slice(0, maxLines);
          const last = lines[maxLines - 1] ?? '';
          let ell = last;
          while (ell && ctx.measureText(`${ell}…`).width > maxW) ell = ell.slice(0, -1);
          lines[maxLines - 1] = `${ell}…`;
        }
        // Allineamento orizzontale: 'center' / 'right' usano textAlign del
        // canvas + ancora di posizione spostata. Default 'left' invariato.
        const align = el.align ?? 'left';
        const prevAlign = ctx.textAlign;
        if (align === 'center') {
          ctx.textAlign = 'center';
          const cx = x + maxW / 2;
          lines.forEach((ln, i) => ctx.fillText(ln, cx, y + i * lineH));
        } else if (align === 'right') {
          ctx.textAlign = 'right';
          const rx = x + maxW;
          lines.forEach((ln, i) => ctx.fillText(ln, rx, y + i * lineH));
        } else {
          lines.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineH));
        }
        ctx.textAlign = prevAlign;
      } else {
        ctx.fillText(txt, x, y);
      }
      continue;
    }

    // Codes are drawn last, directly on printer dots (never through the
    // historical Clabel scale used by text). See the code pass below.
    if (el.type === 'qr') continue;

    if (el.type === 'expiry_weekday') {
      const day = expiryWeekday(data.expires_at);
      if (day) drawExpiryWeekday(ctx, day, x, y, mmToDots(el.width_mm ?? 12), mmToDots(el.height_mm ?? 12));
      continue;
    }

    if (el.type === 'line') {
      ctx.fillRect(x, y, mmToDots(el.width_mm ?? 0), Math.max(1, mmToDots(el.height_mm ?? 0.3)));
      continue;
    }

    if (el.type === 'rect') {
      const w = mmToDots(el.width_mm ?? 0);
      const h = mmToDots(el.height_mm ?? 0);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#000';
      ctx.strokeRect(x + 1, y + 1, Math.max(1, w - 2), Math.max(1, h - 2));
      continue;
    }

    if (el.type === 'barcode') {
      // Fallback: nessun encoder Code128 lato app — stampiamo il dato in
      // chiaro così l'informazione non si perde (i template di default non
      // usano barcode).
      const d = resolveText(el, data);
      if (!d) continue;
      ctx.font = `400 ${ptToPx(6)}px ${FONT_STACK}`;
      ctx.fillText(d, x, y);
      continue;
    }
    // 'image': non supportato (nessun template lo usa).
  }

  for (const el of template.elements.filter(element => element.type === 'qr')) {
    const value = resolveText(el, data);
    if (!value) continue;
    const rectangular = el.qr_format === 'rmqr';
    let code: HTMLCanvasElement | undefined;
    let symbol: QrSym | undefined;
    try {
      if (rectangular) code = rectangularQr(value);
      else symbol = qrCreate(value);
    } catch (error) {
      const message = toUserMessage(error, 'Impossibile generare il codice: verifica il contenuto o riducine la lunghezza.');
      if (!options.onIssue) throw Object.assign(new UserFacingError(message), { cause: error });
      issue(message);
      continue;
    }
    // Four blank modules on each side of a standard QR. rMQR's encoder
    // already includes its two-module quiet zone.
    const columns = code?.width ?? symbol!.modules.size + 8;
    const rows = code?.height ?? columns;
    const width = mmToDots(el.width_mm ?? (rectangular ? 24 : 20));
    const height = rectangular ? mmToDots(el.height_mm ?? 9) : width;
    const cell = Math.min(width / columns, height / rows);
    const minCell = qrMinimumCell(dpi);
    const transform = ctx.getTransform();
    const x = Math.round(transform.a * mmToDots(el.x_mm) + transform.e);
    const y = Math.round(transform.d * mmToDots(el.y_mm) + transform.f);
    const label = rectangular ? 'rMQR' : 'QR';
    if (cell < minCell) {
      issue(`${label}: dimensione ridotta. Consigliati almeno ${(columns * minCell * MM_PER_INCH / dpi).toFixed(1)} × ${(rows * minCell * MM_PER_INCH / dpi).toFixed(1)} mm, bordo bianco incluso. La stampa resta disponibile.`);
    }
    if (x < 0 || y < 0 || x + width > W || y + height > H) {
      issue(`${label}: il riquadro esce dall'etichetta. Spostalo verso il centro per evitare tagli. La stampa resta disponibile.`);
    }
    const visibleW = Math.max(0, Math.min(x + width, W) - Math.max(0, x));
    const visibleH = Math.max(0, Math.min(y + height, H) - Math.max(0, y));
    const pixels = visibleW && visibleH ? ctx.getImageData(Math.max(0, x), Math.max(0, y), visibleW, visibleH).data : [];
    if (pixels.some((channel, index) => index % 4 !== 3 && channel < 200)) {
      issue(`${label}: il riquadro copre altri campi. Il QR ha priorità: la stampa resta disponibile, con il bordo bianco protetto.`);
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#fff';
    ctx.fillRect(x, y, width, height);
    const left = x + Math.round((width - columns * cell) / 2);
    const top = y + Math.round((height - rows * cell) / 2);
    if (code) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(code, left, top, Math.round(columns * cell), Math.round(rows * cell));
    } else {
      ctx.fillStyle = '#000';
      const n = symbol!.modules.size;
      for (let row = 0; row < n; row += 1) {
        for (let col = 0; col < n; col += 1) {
          if (symbol!.modules.data[row * n + col]! & 1) {
            // Round module boundaries to printer dots, not the scale itself:
            // every requested size fills its box, without gray edges or a size jump.
            const x0 = Math.round((col + 4) * cell);
            const y0 = Math.round((row + 4) * cell);
            ctx.fillRect(left + x0, top + y0,
              Math.round((col + 5) * cell) - x0, Math.round((row + 5) * cell) - y0);
          }
        }
      }
    }
    ctx.restore();
  }
  return cv;
}

export interface Packed { bytes: Uint8Array; rowBytes: number; width: number; height: number; }

// Canvas → bitmap 1-bit packed MSB-first, riga allineata al byte.
// Convenzione TSPL BITMAP: bit 1 = bianco, bit 0 = punto nero.
// (Per Niimbot serve la convenzione INVERSA — bit 1 = nero — si inverte
// nel driver dopo aver chiamato questa.)
export function canvasTo1bit(cv: HTMLCanvasElement): Packed {
  const W = cv.width;
  const H = cv.height;
  const ctx = cv.getContext('2d');
  const rowBytes = Math.ceil(W / 8);
  const bytes = new Uint8Array(rowBytes * H);
  bytes.fill(0xff);
  if (!ctx) return { bytes, rowBytes, width: W, height: H };
  const img = ctx.getImageData(0, 0, W, H).data;
  for (let py = 0; py < H; py += 1) {
    for (let pxi = 0; pxi < W; pxi += 1) {
      const i = (py * W + pxi) * 4;
      const lum = 0.299 * img[i]! + 0.587 * img[i + 1]! + 0.114 * img[i + 2]!;
      if (lum < 128) {
        bytes[py * rowBytes + (pxi >> 3)]! &= ~(0x80 >> (pxi & 7));
      }
    }
  }
  return { bytes, rowBytes, width: W, height: H };
}

/**
 * Job TSPL completo: header + comando BITMAP + raster binario + PRINT.
 * Ritorna i byte grezzi (Uint8Array) da inviare al transport.
 */
export function buildTsplBitmapJob(
  template: LabelTemplate,
  data: Record<string, unknown>,
  copies = 1,
  options: RasterOptions = {},
): Uint8Array {
  const cv = renderLabelCanvas(template, data, options);
  const { bytes, rowBytes, height } = canvasTo1bit(cv);
  const header =
    `SIZE ${template.width_mm} mm,${template.height_mm} mm\r\n` +
    'GAP 2 mm,0\r\n' +
    'DIRECTION 0\r\n' +
    'CLS\r\n' +
    `BITMAP 0,0,${rowBytes},${height},${options.bitmapMode ?? 0},`;
  const footer = `\r\nPRINT ${Math.max(1, copies)},1\r\n`;
  const head = new TextEncoder().encode(header);
  const foot = new TextEncoder().encode(footer);
  const job = new Uint8Array(head.length + bytes.length + foot.length);
  job.set(head, 0);
  job.set(bytes, head.length);
  job.set(foot, head.length + bytes.length);
  return job;
}
